import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { signIn, initiateGoogleOAuth } from '../api/auth'
import { Loader2, Mail, Lock } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import Badge from '../components/ui/badge'
import { useQueryClient } from '@tanstack/react-query'
import { getLastAuthMethod, setLastAuthMethod } from '../lib/auth-storage'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'

const SignInPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [googleLoading, setGoogleLoading] = useState(false)
  const location = useLocation()
  const fromLocation = (location.state as any)?.from
  const fromPath = fromLocation ? (fromLocation.pathname + (fromLocation.search || '')) : '/'

  // Redirect to dashboard if already signed in
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate(fromPath, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, fromPath])
  
  const { register, handleSubmit, formState, setError } = useForm<{ email: string; password: string; rememberMe: boolean }>({
    defaultValues: { email: '', password: '', rememberMe: true },
  })

  const lastAuthMethod = getLastAuthMethod()
  
  const onSubmit = async (values: { email: string; password: string; rememberMe: boolean }) => {
    try {
  await signIn(values)
      setLastAuthMethod('email')
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] })
  navigate(fromPath, { replace: true })
    } catch (e: unknown) {
      const error = e as { message?: string }
      setError('root', { message: error?.message ?? 'Sign in failed' })
    }
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    try {
      const result = await initiateGoogleOAuth()
      setLastAuthMethod('google')
      window.location.href = result.url
    } catch (e: unknown) {
      const error = e as { message?: string }
      setError('root', { message: error?.message ?? 'Google sign-in failed' })
      setGoogleLoading(false)
    }
  }

  const rootError = formState?.errors?.root?.message as string | undefined

  // Show loading state while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="flex items-center gap-3" role="status" aria-live="polite">
          <Loader2 className="h-5 w-5 animate-spin text-brand-600" aria-hidden="true" />
          <p className="text-sm text-foreground/70 tracking-tight">Checking authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white border border-black/10 rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-semibold mb-1">Welcome back</h2>
          <p className="text-foreground/60 mb-6">Sign in to continue.</p>

          {rootError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{rootError}</div>
          )}

          <label className="block text-sm font-medium mb-1" htmlFor="email">Email</label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            leftIcon={<Mail className="h-4 w-4" aria-hidden="true" />}
            {...register('email', { required: 'Email is required' })}
          />
          {formState.errors.email && (
            <p className="mt-1 text-sm text-red-600">{formState.errors.email.message}</p>
          )}

          <label className="block text-sm font-medium mb-1 mt-4" htmlFor="password">Password</label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            leftIcon={<Lock className="h-4 w-4" aria-hidden="true" />}
            {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
          />
          {formState.errors.password && (
            <p className="mt-1 text-sm text-red-600">{formState.errors.password.message}</p>
          )}

          <label className="mt-4 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" {...register('rememberMe')} />
            Remember me
          </label>

          <Button type="submit" className="w-full mt-6" disabled={formState.isSubmitting}>
            {formState.isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            <span>{formState.isSubmitting ? 'Signing in…' : 'Sign in'}</span>
            {lastAuthMethod === 'email' && (
              <Badge variant="success" className="ml-2">Last used</Badge>
            )}
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">or</span>
            </div>
          </div>

          <Button 
            type="button" 
            variant="secondary" 
            className="w-full" 
            disabled={googleLoading || formState.isSubmitting}
            onClick={handleGoogleSignIn}
          >
            {googleLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>{googleLoading ? 'Redirecting…' : 'Continue with Google'}</span>
            {lastAuthMethod === 'google' && (
              <Badge variant="success" className="ml-2">Last used</Badge>
            )}
          </Button>

          {/* Sign up hint/link */}
          <p className="text-xs text-foreground/80 mt-4 text-center">
            Don&apos;t have an account yet?{' '}
            <a
              href={
                typeof window !== 'undefined' && window.location.hostname.includes('localhost')
                  ? 'http://localhost:3000/?signup=true'
                  : window.location.hostname.includes('staging')
                    ? 'https://staging.podcast-generator.ai/?signup=true'
                    : 'https://podcast-generator.ai/?signup=true'
              }
              className="text-brand-600 hover:underline cursor-pointer"
            >
              Sign up
            </a>
          </p>
        </form>
      </div>
    </div>
  )
}

export default SignInPage
